import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  UserPlus,
  Footprints,
  ShieldAlert,
  Bell,
  FastForward,
  Ban,
  CircleDot,
  Info,
  HelpCircle,
  Layers,
  TrafficCone,
  Activity,
  Navigation,
  Gauge,
  ArrowLeftRight,
  ParkingCircle,
  Split,
  Users,
  TrainFront,
  Mountain,
  Bus,
  Lightbulb,
  Truck,
  Package,
  Bike,
  FileText,
  HeartPulse,
  RotateCcw,
  Loader2,
  ChevronRight,
  ArrowLeft,
  Search,
  X,
} from "lucide-react";
import { topicsAPI } from "../api";
import { useLang } from "../context/LangContext";

const TOPIC_ICONS: Record<number, any> = {
  1: BookOpen,
  2: UserPlus,
  3: Footprints,
  4: ShieldAlert,
  5: Bell,
  6: FastForward,
  7: Ban,
  8: CircleDot,
  9: Info,
  10: HelpCircle,
  11: Layers,
  12: TrafficCone,
  13: Activity,
  14: TrafficCone,
  15: UserPlus,
  16: Bell,
  17: Navigation,
  18: Layers,
  19: Gauge,
  20: ArrowLeftRight,
  21: ParkingCircle,
  22: Split,
  23: TrafficCone,
  24: Navigation,
  25: ArrowLeftRight,
  26: RotateCcw,
  27: Users,
  28: TrainFront,
  29: FastForward,
  30: UserPlus,
  31: Mountain,
  32: Bus,
  33: Lightbulb,
  34: Truck,
  35: UserPlus,
  36: Users,
  37: Package,
  38: Bike,
  39: FileText,
  40: Ban,
  41: ShieldAlert,
  42: HeartPulse,
};

const TopicsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, lang } = useLang();

  const [topics, setTopics] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // Sahifalash holati ref'larda saqlanadi — setState'ning asinxron/qayta
  // ishga tushishi (StrictMode double-invoke, scroll+auto-fill racing)
  // bir sahifani ikki marta yoki umuman so'ramay qoldirishining oldini olish uchun.
  const isFetching = useRef(false);
  const pageRef = useRef(0);
  const hasMoreRef = useRef(true);

  // ─── Qidiruv (mavzu nomi bo'yicha) ──────────────────────────────────
  // Mavzular soni kichik (~40 ta) bo'lgani uchun qidiruvni butunlay
  // client-side qilamiz — birinchi marta qidiruv boshlanganda BARCHA
  // mavzular (katta limit bilan) bir marta yuklab olinadi va keshlanadi.
  const [search, setSearch] = useState("");
  const [allTopics, setAllTopics] = useState<any[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchFetchedRef = useRef(false);

  useEffect(() => {
    const q = search.trim();
    if (!q) return;
    if (searchFetchedRef.current) return;
    searchFetchedRef.current = true;
    setSearchLoading(true);
    topicsAPI
      .getAll(1, 200, lang)
      .then((r) => {
        const d = r.data || {};
        const incoming = d.data || d.topics || (Array.isArray(d) ? d : []);
        setAllTopics(incoming);
      })
      .catch(() => {})
      .finally(() => setSearchLoading(false));
  }, [search, lang]);

  // Til o'zgarsa, keshlangan "barcha mavzular" eskiradi — qayta yuklanadi
  useEffect(() => {
    searchFetchedRef.current = false;
    setAllTopics(null);
  }, [lang]);

  const filteredTopics = search.trim()
    ? (allTopics ?? topics).filter((topic) => {
        const q = search.trim().toLowerCase();
        return (
          topic?.name_oz?.toLowerCase().includes(q) ||
          topic?.name_uz?.toLowerCase().includes(q) ||
          topic?.name_ru?.toLowerCase().includes(q) ||
          // Backend /topics javobida faqat "name" (joriy tilga moslab
          // oldindan tanlangan) keladi — name_oz/uz/ru maydoni yo'q,
          // shu bois asosiy tekshiruv aslida shu bo'lishi kerak.
          topic?.name?.toLowerCase().includes(q)
        );
      })
    : topics;

  const loadNextPage = useCallback(async () => {
    if (isFetching.current || !hasMoreRef.current) return;
    if (search.trim()) return; // qidiruv rejimida oddiy sahifalash to'xtaydi
    isFetching.current = true;
    setLoading(true);
    const pageNum = pageRef.current + 1;
    try {
      const r = await topicsAPI.getAll(pageNum, 15, lang);
      const d = r.data || {};
      const incoming = d.data || d.topics || (Array.isArray(d) ? d : []);
      setTopics((prev) =>
        pageNum === 1 ? incoming : [...prev, ...incoming],
      );
      pageRef.current = pageNum;

      const totalPages = Number(
        d.totalPages ||
          d.meta?.totalPages ||
          Math.ceil((d.total || d.meta?.total || 42) / 15) ||
          1,
      );
      hasMoreRef.current = pageNum < totalPages;
      setHasMore(hasMoreRef.current);
    } catch (err) {
      console.error(t("topicsLoadError"), err);
      hasMoreRef.current = false;
      setHasMore(false);
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  }, [lang, t, search]);

  useEffect(() => {
    pageRef.current = 0;
    hasMoreRef.current = true;
    setHasMore(true);
    setTopics([]);
    loadNextPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (loading || !hasMore) return;
    if (document.documentElement.scrollHeight <= window.innerHeight) {
      loadNextPage();
    }
  }, [topics, loading, hasMore, loadNextPage]);

  useEffect(() => {
    const onScroll = () => {
      const threshold = 120;
      const isBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - threshold;
      if (isBottom) loadNextPage();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [loadNextPage]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: "rgba(10,14,26,.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--border)",
          height: "calc(52px + env(safe-area-inset-top))",
          paddingTop: "env(safe-area-inset-top)",
        }}>
        <div
          className="max-w-[520px] lg:max-w-[1040px]"
          style={{
            margin: "0 auto",
            padding: "0 16px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--muted)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              fontWeight: 600,
            }}>
            <ArrowLeft size={16} /> {t("back")}
          </button>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
            {t("testsByTopics")}
          </span>
        </div>
      </div>

      <div
        className="max-w-[520px] lg:max-w-[1040px]"
        style={{ margin: "0 auto", padding: "68px 16px 32px" }}>
        {/* Qidiruv */}
        <div style={{ position: "relative", marginBottom: 16 }}>
          <Search
            size={16}
            style={{
              position: "absolute",
              left: 14,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--muted)",
            }}
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={
              lang === "ru" ?
                "Тема бўйича қидириш..."
              : lang === "uz" ?
                "Мавзу номи бўйича қидириш..."
              : "Mavzu nomi bo'yicha qidirish..."
            }
            style={{
              width: "100%",
              padding: "11px 40px",
              borderRadius: 12,
              background: "var(--card)",
              border: "1px solid var(--border)",
              color: "#E2E8F0",
              fontSize: 13,
              outline: "none",
            }}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "transparent",
                border: "none",
                color: "var(--muted)",
                cursor: "pointer",
                display: "flex",
              }}>
              <X size={16} />
            </button>
          )}
        </div>

        {search.trim() && searchLoading && (
          <div style={{ display: "flex", justifyContent: "center", padding: 30 }}>
            <Loader2 className="animate-spin text-blue-500" size={22} />
          </div>
        )}

        {search.trim() && !searchLoading && filteredTopics.length === 0 && (
          <p
            style={{
              textAlign: "center",
              color: "var(--muted)",
              fontSize: 13,
              padding: "30px 0",
            }}>
            Hech narsa topilmadi
          </p>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
          {filteredTopics.map((topic, i) => {
            const pct =
              topic.userStats ? Math.round(topic.userStats.bestScore)
              : topic.total ? Math.round((topic.progress / topic.total) * 100)
              : 0;
            const IconComponent = TOPIC_ICONS[topic.id] || BookOpen;
            const title =
              topic?.[`name_${lang}`] ||
              topic?.name_oz ||
              topic?.name_uz ||
              topic?.name_ru ||
              topic?.name ||
              topic?.title ||
              "";
            return (
              <button
                key={`${topic.id}-${i}`}
                onClick={() => navigate(`/quiz/topic/${topic.id}`)}
                className="fade-up"
                style={{
                  opacity: 0,
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "16px",
                  borderRadius: 16,
                  background: "var(--card)",
                  border: "1px solid var(--border)",
                  cursor: "pointer",
                  transition: "all .2s",
                  textAlign: "left",
                  animation: "fadeUp 0.4s ease forwards",
                  animationDelay: `${(i % 15) * 30}ms`,
                }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: "rgba(59,130,246,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}>
                  <IconComponent size={22} color="#3B82F6" />
                </div>

                <div style={{ flex: 1, overflow: "hidden" }}>
                  <p
                    style={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 14,
                      marginBottom: 4,
                    }}>
                    {title}
                  </p>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div
                      style={{
                        flex: 1,
                        height: 5,
                        background: "rgba(255,255,255,0.05)",
                        borderRadius: 99,
                        overflow: "hidden",
                      }}>
                      <div
                        style={{
                          height: "100%",
                          width: `${pct}%`,
                          background:
                            pct === 100 ? "#22C55E" : (
                              "linear-gradient(90deg, #3B82F6, #60A5FA)"
                            ),
                          borderRadius: 99,
                          transition: "width 0.5s ease-out",
                        }}
                      />
                    </div>
                    <span
                      style={{
                        color: pct === 100 ? "#22C55E" : "#60A5FA",
                        fontSize: 11,
                        fontWeight: 800,
                        flexShrink: 0,
                        minWidth: 35,
                      }}>
                      {pct}%
                    </span>
                  </div>
                </div>

                <ChevronRight
                  size={18}
                  style={{ color: "var(--muted)", opacity: 0.5 }}
                />
              </button>
            );
          })}
        </div>

        {loading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "20px",
            }}>
            <Loader2 className="animate-spin text-blue-500" size={24} />
          </div>
        )}
      </div>
    </div>
  );
};

export default TopicsPage;
